import {DateTime, Interval} from 'luxon';
import React, {FC, useEffect, useRef, useState} from 'react';

import {reloadBrowser} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {FrontConfig} from '../../../../../libs/shared/core/src/helpers/bundler/bundlerDefineConfig';
import {spyOnHttpResponses} from '../../../../../libs/shared/core/src/services/http/httpBase';
import {logger} from '../../../../../libs/shared/core/src/services/logger/logger';
import {registerShowUpdateBannerFunction} from '../../../../services/electron/electronBridgeActions';
import {quitAndInstall} from '../../../../services/electron/electronBridgeOutbound';
import {isWaitingServiceWorker} from '../../../../services/serviceWorker/bridge/serviceWorkerBridge';
import {NewVersionBanner, NewVersionBannersEnum} from './newVersionBanner';

/*
 * Hook.
 */

function useNewVersionBanner() {
  const wasClosedByUser = useRef(false);
  const [bannerType, setBannerType] = useState<NewVersionBannersEnum | null>(null);

  // Start spying on ANY http response on mount and display the banner once if the version returned by the backend
  // changed since the app was loaded. Cleanup the spying on unmount for the sake of completion, even though it should not happen.
  useEffect(() => {
    const stopSpying = spyOnHttpResponses((response) => {
      if (
        !wasClosedByUser.current &&
        bannerType !== NewVersionBannersEnum.NEW_ELECTRON_VERSION &&
        shouldShowClientRefreshBanner()
      ) {
        setBannerType(NewVersionBannersEnum.NEW_FRONTEND_VERSION);
      }
    });

    registerShowUpdateBannerFunction(() => {
      if (!wasClosedByUser.current) {
        setBannerType(NewVersionBannersEnum.NEW_ELECTRON_VERSION);
      }
    });

    return stopSpying;
  }, [bannerType]);

  function onRequestClose() {
    setBannerType(null);
    wasClosedByUser.current = true;
  }

  return {bannerType, onRequestClose};
}

/**
 * Show the update banner if the client is older than 5 days and a new version is available.
 */
function shouldShowClientRefreshBanner() {
  const buildDate = new Date(FrontConfig.BUILD_TIMESTAMP);
  const buildDateTime = DateTime.fromJSDate(buildDate);
  const buildAge = Interval.fromDateTimes(buildDateTime, DateTime.now()).length('days');
  return isWaitingServiceWorker() && buildAge > 5;
}

/*
 * Component.
 */

function bannerStateToReloadFunction(bannerType: NewVersionBannersEnum) {
  switch (bannerType) {
    case NewVersionBannersEnum.NEW_FRONTEND_VERSION:
      return reloadBrowser;
    case NewVersionBannersEnum.NEW_ELECTRON_VERSION:
    default:
      return () => {
        logger.info('Attempting to upgrade Electron app');
        quitAndInstall();
      };
  }
}

export const NewVersionBannerContainer: FC = () => {
  const {bannerType, onRequestClose} = useNewVersionBanner();
  if (!bannerType) {
    return null;
  }

  return (
    <NewVersionBanner
      onDismissClick={onRequestClose}
      onReloadClick={bannerStateToReloadFunction(bannerType)}
      bannerType={bannerType}
    />
  );
};
